/**
 * The table-profile review screen: header detection through the review gate.
 *
 * A misidentified header row shifts every column label by one and mislabels the
 * whole sheet, so the same rule as everywhere else in this codebase applies:
 * confidence never auto-commits. This screen shows exactly what ingest would
 * render — the composed row text, built by the same function ingest itself calls —
 * so a shifted header is visible as mislabelled data before a reviewer approves it,
 * rather than something only a re-ingest would reveal.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type TableColumn, type TableProfile } from "../lib/api";
import { go } from "../lib/route";

export function TableProfileReview({
  datasetId,
  fileId,
}: {
  datasetId: string;
  fileId: string;
}) {
  const [profile, setProfile] = useState<TableProfile | null>(null);
  const [rows, setRows] = useState<
    { sheet: string | null; row_no: number; text: string }[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const preview = await api.tableProfilePreview(datasetId, fileId);
      setProfile(preview.profile);
      setRows(preview.rows);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.detail
          : cause instanceof Error
            ? cause.message
            : String(cause),
      );
    }
  }, [datasetId, fileId]);

  useEffect(() => {
    void load();
  }, [load]);

  function setLabel(letter: string, label: string) {
    if (!profile) return;
    const column = profile.columns[letter];
    if (!column) return;
    setProfile({
      ...profile,
      columns: { ...profile.columns, [letter]: { label, type: column.type } },
    });
  }

  function setType(letter: string, type: TableColumn["type"]) {
    if (!profile) return;
    const column = profile.columns[letter];
    if (!column) return;
    setProfile({
      ...profile,
      columns: { ...profile.columns, [letter]: { label: column.label, type } },
    });
  }

  async function repreview() {
    if (!profile) return;
    setBusy(true);
    setError(null);
    try {
      const preview = await api.previewEditedTableProfile(datasetId, fileId, profile);
      setRows(preview.rows);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (!profile) return;
    setBusy(true);
    setError(null);
    try {
      await api.approveTableProfile(datasetId, profile);
      setApproved(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <h1>Header review</h1>
      <p className="mono" style={{ color: "var(--ink-faint)", margin: 0 }}>
        Detection proposes; nothing here labels the corpus until you approve it. The
        rows below are exactly what ingest would write.
      </p>

      <p>
        <a href={`#/d/${datasetId}/files`} data-testid="table-review-back">
          ← Back to files
        </a>
      </p>

      {error && (
        <p data-testid="table-review-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {approved && (
        <p data-testid="table-review-approved" style={{ color: "var(--ink)" }}>
          Approved. The labelled generation is being ingested —{" "}
          <a href={`#/d/${datasetId}/search`}>search this dataset</a> once it drains.
        </p>
      )}

      {profile && !approved && (
        <>
          {/* A label field and a type picker in every row: a sheet with a dozen
              columns is wider than a phone before the first one is filled in. Same
              containment as everywhere else — the table scrolls, the page does not. */}
          <div className="table-scroll">
            <table className="grid" data-testid="table-profile-columns">
              <thead>
                <tr>
                  <th>Column</th>
                  <th>Label</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(profile.columns).map(([letter, column]) => (
                  <tr key={letter} data-testid="table-profile-column-row">
                    <td className="mono">{letter}</td>
                    <td>
                      <input
                        aria-label={`Label for column ${letter}`}
                        data-testid={`column-label-${letter}`}
                        value={column.label}
                        onChange={(e) => setLabel(letter, e.target.value)}
                      />
                    </td>
                    <td>
                      <select
                        aria-label={`Type for column ${letter}`}
                        value={column.type}
                        onChange={(e) => setType(letter, e.target.value as TableColumn["type"])}
                      >
                        <option value="string">string</option>
                        <option value="number">number</option>
                        <option value="date">date</option>
                        <option value="bool">bool</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row">
            <button type="button" onClick={() => void repreview()} disabled={busy}>
              {busy ? "…" : "Re-preview"}
            </button>
            <button
              className="primary"
              type="button"
              data-testid="table-profile-approve"
              onClick={() => void approve()}
              disabled={busy}
            >
              {busy ? "…" : "Approve"}
            </button>
          </div>

          <h2>Preview</h2>
          <p className="mono" style={{ color: "var(--ink-faint)" }}>
            The first {rows?.length ?? 0} row(s), rendered with the labels above.
          </p>
          <ol className="hits" data-testid="table-profile-preview-rows">
            {rows?.map((row) => (
              <li key={`${row.sheet ?? ""}-${row.row_no}`} data-testid="table-profile-preview-row">
                <span className="eyebrow">
                  {row.sheet ? `${row.sheet} · ` : ""}row {row.row_no}
                </span>
                <span className="hit-text">{row.text}</span>
              </li>
            ))}
          </ol>
        </>
      )}
    </main>
  );
}

// Kept for callers that want to navigate here directly rather than via a link.
export function tableProfileReviewPath(datasetId: string, fileId: string): string {
  return `/d/${datasetId}/files/table-profile/${fileId}`;
}

export function goToTableProfileReview(datasetId: string, fileId: string): void {
  go(tableProfileReviewPath(datasetId, fileId));
}
